import type { PolicyRule } from "../types/domain";

export const RELATED_REQUIREMENTS_PACK = {
  id: "my-construction-regulatory-overlay-2026",
  title: "Malaysian construction safety and approval overlay",
  version: "1.0.0-source-mapped",
  status: "source-mapped",
  notes:
    "Federal occupational safety duties and selected state approval gateways are mapped to official sources and activated by recorded project facts.",
} as const;

const reviewState = "source-mapped" as const;

const oshaUrl =
  "https://dosh.gov.my/wp-content/uploads/2025/01/Occupational-Safety-and-Health-Act-1994-Act-514_Reprint-Version-1.6.2024_English.pdf";
const usechhUrl =
  "https://dosh.gov.my/wp-content/uploads/2026/04/Occupational-Safety-and-Health-Use-and-Standards-of-Exposure-of-Chemicals-Hazardous-to-Health-Regulations-2000.pdf";
const chraManualUrl =
  "https://dosh.gov.my/wp-content/uploads/2025/01/A-Manual-of-Recommended-Practice-on-Assessment-of-the-Health-Risks-Arising-from-the-Use-of-Chemicals-Hazardous-to-Health-at-the-Workplace-3rd-Edition-First-Reprint-2018.pdf";
const classUrl =
  "https://dosh.gov.my/wp-content/uploads/2026/04/Occupational-Safety-and-Health-Classification-Labelling-and-Safety-Data-Sheet-of-Hazardous-Chemicals-Regulations-2013.pdf";
const noiseUrl =
  "https://dosh.gov.my/wp-content/uploads/2025/01/Peraturan-Peraturan-Keselamatan-dan-Kesihatan-Pekerjaan-Pendedahan-Bising-2019.pdf";
const noiseIcopUrl =
  "https://dosh.gov.my/wp-content/uploads/2025/01/Tataamalan-Industri-Bagi-Pengurusan-Pendedahan-Bising-Pekerjaan-dan-Pemuliharaan-Pendengaran-2019.pdf";
const nadopodUrl =
  "https://dosh.gov.my/wp-content/uploads/2026/02/GP-NADOPOD-2005-BI.pdf";
const plantCofUrl =
  "https://dosh.gov.my/wp-content/uploads/2025/01/Peraturan%E2%80%93Peraturan-Keselamatan-dan-Kesihatan-Pekerjaan-Loji-Yang-Menghendaki-Perakuan-Kelayakan-2024.pdf";
const specialSchemeUrl =
  "https://dosh.gov.my/wp-content/uploads/2025/01/Peraturan-Peraturan-Keselamatan-dan-Kesihatan-Pekerjaan-Skim-Pemeriksaan-Khas-2025.pdf";
const cimahUrl =
  "https://intranet.dosh.gov.my/regulation/regulations-under-occupational-safety-and-health-act-1994-act-514/527-07-occupational-safety-and-health-control-of-industrial-major-accident-hazards-regulations-1996/file";
const committeeUrl =
  "https://intranet.dosh.gov.my/regulation/regulations-under-occupational-safety-and-health-act-1994-act-514/526-06-occupational-safety-and-health-safety-and-health-committee-regulations-1996/file";
const cidbUrl =
  "https://www.cidb.gov.my/wp-content/uploads/2022/12/Act-520-As-at-1-October-2015.pdf";
const sarawakBuildingBylawsUrl =
  "https://lawnet.sarawak.gov.my/lawnet_file/Subsidiary/SUB_BuildingByLawSchedule.pdf";
const sarawakEnvironmentUrl =
  "https://www.nreb.gov.my/web/attachment/show/?docid=QitOZ3crdVNCWm5GdHhZNS9xcGRZdz09OjpdxI3QTYV_3ocyC7EsYD2C";

export const RELATED_POLICY_RULES: PolicyRule[] = [
  {
    id: "MY-USECHH-R5",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Use and Standards of Exposure of Chemicals Hazardous to Health) Regulations 2000",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulation 5",
    title: "Chemical register and current safety data",
    dutyHolder: "contractor",
    requirement:
      "Identify every chemical hazardous to health used at the workplace and maintain an accessible, current register covering quantities, work areas, processes, suppliers and safety data sheets.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Chemical register",
      "Current safety data sheets",
      "Chemical quantity and use-location schedule",
    ],
    sourceUrl: `${usechhUrl}#page=5`,
    sourceLabel: "USECHH 2000 · Regulation 5 · PDF p. 5",
    reviewState,
  },
  {
    id: "MY-USECHH-R9",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Use and Standards of Exposure of Chemicals Hazardous to Health) Regulations 2000",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 9–13",
    title: "Written chemical health risk assessment",
    dutyHolder: "contractor",
    requirement:
      "Do not carry out work likely to expose an employee to a chemical hazardous to health until a written assessment is completed by a registered assessor and the report is retained and made available.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Current CHRA report",
      "Registered assessor evidence",
      "CHRA recommendation action register",
      "CHRA review trigger record",
    ],
    sourceUrl: `${usechhUrl}#page=6`,
    sourceLabel: "USECHH 2000 · Regulations 9–13 · PDF pp. 6–7",
    reviewState,
  },
  {
    id: "MY-USECHH-R14",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Use and Standards of Exposure of Chemicals Hazardous to Health) Regulations 2000",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 14–19",
    title: "Chemical exposure controls and engineering assurance",
    dutyHolder: "contractor",
    requirement:
      "Implement CHRA actions promptly and control exposure using the prescribed hierarchy, with designed, commissioned, inspected and tested engineering controls and controlled records.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Chemical exposure control plan",
      "Hierarchy-of-control decision record",
      "Engineering control design and commissioning",
      "Monthly inspection and annual test records",
    ],
    sourceUrl: `${usechhUrl}#page=8`,
    sourceLabel: "USECHH 2000 · Regulations 14–19 · PDF pp. 8–10",
    reviewState,
  },
  {
    id: "MY-USECHH-R22",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Use and Standards of Exposure of Chemicals Hazardous to Health) Regulations 2000",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 22–25",
    title: "Chemical information, training and accessible SDS",
    dutyHolder: "contractor",
    requirement:
      "Provide documented information, instruction and training on chemical risks and precautions, repeat it at prescribed change intervals, and keep current safety data sheets accessible near use locations.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Chemical training matrix",
      "Training and retraining records",
      "SDS accessibility inspection",
      "Chemical labelling inspection",
    ],
    sourceUrl: `${usechhUrl}#page=11`,
    sourceLabel: "USECHH 2000 · Regulations 22–25 · PDF pp. 11–12",
    reviewState,
  },
  {
    id: "MY-USECHH-R26",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Use and Standards of Exposure of Chemicals Hazardous to Health) Regulations 2000",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 26–30",
    title: "Exposure monitoring, health surveillance and records",
    dutyHolder: "contractor",
    requirement:
      "Where the assessment requires it, conduct exposure monitoring through a hygiene technician, implement health surveillance through an occupational health doctor and retain the prescribed records.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Exposure monitoring programme",
      "Hygiene technician reports",
      "Health surveillance determination and programme",
      "Statutory retention schedule",
    ],
    sourceUrl: `${usechhUrl}#page=12`,
    sourceLabel: "USECHH 2000 · Regulations 26–30 · PDF pp. 12–14",
    reviewState,
  },
  {
    id: "MY-CHRA-MANUAL",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "DOSH Manual of Recommended Practice on CHRA, 3rd Edition",
    jurisdiction: "Malaysia · Federal guidance",
    regulation: "CHRA methodology",
    title: "CHRA quality and action tracking",
    dutyHolder: "all",
    requirement:
      "Use the DOSH CHRA methodology to structure exposure groups, hazard rating, exposure rating, risk determination, adequacy of controls and prioritised recommendations.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Similar exposure group register",
      "CHRA risk determination worksheet",
      "Control adequacy review",
      "Recommendation close-out evidence",
    ],
    sourceUrl: `${chraManualUrl}#page=28`,
    sourceLabel: "DOSH CHRA Manual · methodology",
    reviewState,
  },
  {
    id: "MY-CLASS-R8",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Classification, Labelling and Safety Data Sheet of Hazardous Chemicals) Regulations 2013",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 4–13",
    title: "CLASS label, packaging and safety-data verification",
    dutyHolder: "contractor",
    requirement:
      "Verify that hazardous chemicals supplied to the project have compliant classification, packaging, labels and bilingual current safety data sheets before acceptance and use.",
    trigger: "chemical-use",
    expectedEvidence: [
      "Chemical receipt compliance check",
      "CLASS label inspection",
      "Bilingual SDS verification",
      "Non-conforming chemical quarantine record",
    ],
    sourceUrl: `${classUrl}#page=7`,
    sourceLabel: "CLASS 2013 · Regulations 4–13 · PDF pp. 6–12",
    reviewState,
  },
  {
    id: "MY-NOISE-R3",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Occupational Safety and Health (Noise Exposure) Regulations 2019",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 3–4",
    title: "Excessive-noise identification and risk assessment",
    dutyHolder: "contractor",
    requirement:
      "Identify whether employees may be exposed to excessive noise and, where indicated, appoint a noise risk assessor to complete and review a noise risk assessment.",
    trigger: "noise-exposure",
    expectedEvidence: [
      "Noise hazard identification checklist",
      "Noise risk assessment report",
      "Registered assessor evidence",
      "Noise assessment review record",
    ],
    sourceUrl: `${noiseUrl}#page=5`,
    sourceLabel: "Noise Exposure Regulations 2019 · Regulations 3–4",
    reviewState,
  },
  {
    id: "MY-NOISE-R5",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Occupational Safety and Health (Noise Exposure) Regulations 2019",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 5–7",
    title: "Noise information, reduction and hearing protection",
    dutyHolder: "contractor",
    requirement:
      "Provide information and training, reduce noise using engineering and administrative controls, demarcate hearing-protection zones and manage suitable hearing protection.",
    trigger: "noise-exposure",
    expectedEvidence: [
      "Noise control programme",
      "Engineering-control verification",
      "Hearing-protection zone plan",
      "Training and hearing-protection fit records",
    ],
    sourceUrl: `${noiseUrl}#page=7`,
    sourceLabel: "Noise Exposure Regulations 2019 · Regulations 5–7",
    reviewState,
  },
  {
    id: "MY-NOISE-R8",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Occupational Safety and Health (Noise Exposure) Regulations 2019",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulation 8",
    title: "Audiometric testing and hearing conservation",
    dutyHolder: "contractor",
    requirement:
      "Place exposed employees in an audiometric testing programme, act on abnormal results and retain programme and employee records with appropriate confidentiality.",
    trigger: "noise-exposure",
    expectedEvidence: [
      "Audiometric testing programme",
      "Employee exposure group list",
      "Occupational health follow-up record",
      "Audiometry notification and retention record",
    ],
    sourceUrl: `${noiseUrl}#page=10`,
    sourceLabel: "Noise Exposure Regulations 2019 · Regulation 8",
    reviewState,
  },
  {
    id: "MY-NOISE-ICOP",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Industry Code of Practice for Management of Occupational Noise Exposure and Hearing Conservation 2019",
    jurisdiction: "Malaysia · Federal guidance",
    regulation: "Noise management system",
    title: "Noise-control programme assurance",
    dutyHolder: "all",
    requirement:
      "Maintain an integrated noise programme covering identification, assessment, prioritised engineering control, hearing conservation, training, audiometry, review and records.",
    trigger: "noise-exposure",
    expectedEvidence: [
      "Noise management programme",
      "Significant exposure group register",
      "Noise-control implementation schedule",
      "Annual programme effectiveness review",
    ],
    sourceUrl: `${noiseIcopUrl}#page=12`,
    sourceLabel: "DOSH Noise ICOP 2019",
    reviewState,
  },
  {
    id: "MY-NADOPOD-NOTIFY",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Notification of Accident, Dangerous Occurrence, Occupational Poisoning and Occupational Disease) Regulations 2004",
    jurisdiction: "Malaysia · Federal",
    regulation: "Notification and reporting",
    title: "Statutory event notification and report",
    dutyHolder: "contractor",
    requirement:
      "Classify the event, make the required immediate notification and written report within the applicable statutory route, and retain evidence of submission.",
    trigger: "reportable-event",
    expectedEvidence: [
      "NADOPOD classification decision",
      "Immediate notification record",
      "Prescribed report and submission acknowledgement",
      "Regulator correspondence",
    ],
    sourceUrl: `${nadopodUrl}#page=8`,
    sourceLabel: "DOSH NADOPOD 2005 guidance · reporting workflow",
    reviewState,
  },
  {
    id: "MY-NADOPOD-INVESTIGATE",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Notification of Accident, Dangerous Occurrence, Occupational Poisoning and Occupational Disease) Regulations 2004",
    jurisdiction: "Malaysia · Federal",
    regulation: "Investigation and records",
    title: "Incident investigation and learning close-out",
    dutyHolder: "all",
    requirement:
      "Preserve the event record, investigate immediate and underlying causes, identify failed or absent controls and track corrective and design-learning actions to closure.",
    trigger: "reportable-event",
    expectedEvidence: [
      "Incident investigation report",
      "Root-cause analysis",
      "Corrective action register",
      "Design and control learning review",
    ],
    sourceUrl: `${nadopodUrl}#page=19`,
    sourceLabel: "DOSH NADOPOD 2005 guidance · records and investigation",
    reviewState,
  },
  {
    id: "MY-PLANT-R4",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Plant Requiring Certificate of Fitness) Regulations 2024",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulation 4",
    title: "Plant design verification and installation approval",
    dutyHolder: "client",
    requirement:
      "Obtain design verification and written installation approval, retain the prescribed technical documents and ensure competent installation and adequate foundations before regulated plant is operated.",
    trigger: "regulated-plant",
    expectedEvidence: [
      "Regulated plant register",
      "Design verification approval",
      "Installation approval",
      "Foundation and integrity examination records",
    ],
    sourceUrl: `${plantCofUrl}#page=75`,
    sourceLabel: "Plant COF Regulations 2024 · Regulation 4 · PDF pp. 75–77",
    reviewState,
  },
  {
    id: "MY-PLANT-R5",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Plant Requiring Certificate of Fitness) Regulations 2024",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 5–8",
    title: "Certificate, inspection and maintenance control",
    dutyHolder: "contractor",
    requirement:
      "Do not operate regulated plant without the required certificate, prepare it for statutory inspection and maintain it safely through competent persons and controlled records.",
    trigger: "regulated-plant",
    expectedEvidence: [
      "Current certificate of fitness",
      "Periodical inspection schedule and reports",
      "Competent maintenance agreement",
      "Plant defect and close-out log",
    ],
    sourceUrl: `${plantCofUrl}#page=77`,
    sourceLabel: "Plant COF Regulations 2024 · Regulations 5–8 · PDF pp. 77–81",
    reviewState,
  },
  {
    id: "MY-PLANT-R9",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Plant Requiring Certificate of Fitness) Regulations 2024",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 9–11",
    title: "Repair, dismantling and alteration governance",
    dutyHolder: "contractor",
    requirement:
      "Control regulated-plant repairs, dismantling and alterations through competent persons, written notices or approvals, applicable testing and reinspection before operation.",
    trigger: "regulated-plant",
    expectedEvidence: [
      "Repair or alteration approval",
      "Competent person appointment",
      "Post-work test and examination",
      "Return-to-service approval",
    ],
    sourceUrl: `${plantCofUrl}#page=81`,
    sourceLabel: "Plant COF Regulations 2024 · Regulations 9–11 · PDF pp. 81–83",
    reviewState,
  },
  {
    id: "MY-SSI-R5",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "approval-gateway",
    instrument: "Occupational Safety and Health (Special Scheme of Inspection) Regulations 2025",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 5–14",
    title: "Special scheme eligibility and plant-management system",
    dutyHolder: "client",
    requirement:
      "Where the special scheme pathway is selected, establish the applicable Class A or B plant-management system, competent teams, risk-based inspection plan, application and verification-inspection controls.",
    trigger: "special-scheme-inspection",
    expectedEvidence: [
      "Special scheme eligibility assessment",
      "Plant-management system",
      "Risk-based inspection plan",
      "Approval and verification-inspection record",
    ],
    sourceUrl: `${specialSchemeUrl}#page=36`,
    sourceLabel: "Special Scheme of Inspection Regulations 2025 · Regulations 5–14",
    reviewState,
  },
  {
    id: "MY-CIMAH-R7",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Control of Industrial Major Accident Hazards) Regulations 1996",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 6–8",
    title: "Industrial activity identification and notification",
    dutyHolder: "client",
    requirement:
      "Inventory hazardous substances against the statutory thresholds, classify the installation and submit or update the industrial activity notification before applicable new construction or activity.",
    trigger: "major-hazard",
    expectedEvidence: [
      "Hazardous substance threshold inventory",
      "CIMAH applicability assessment",
      "Industrial activity notification",
      "Change-notification register",
    ],
    sourceUrl: `${cimahUrl}#page=5`,
    sourceLabel: "CIMAH 1996 · Regulations 6–8 · PDF pp. 5–6",
    reviewState,
  },
  {
    id: "MY-CIMAH-R10",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Control of Industrial Major Accident Hazards) Regulations 1996",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 9–11",
    title: "Demonstration of safe operation",
    dutyHolder: "all",
    requirement:
      "For a non-major-hazard installation within scope, maintain evidence that major accident hazards are identified, prevented or mitigated and supported by training, equipment and an updated on-site emergency plan.",
    trigger: "major-hazard",
    expectedEvidence: [
      "Major-accident hazard assessment",
      "Safe-operation demonstration",
      "Control and training records",
      "On-site emergency plan",
    ],
    sourceUrl: `${cimahUrl}#page=6`,
    sourceLabel: "CIMAH 1996 · Regulations 9–11 · PDF pp. 6–7",
    reviewState,
  },
  {
    id: "MY-CIMAH-R14",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument:
      "Occupational Safety and Health (Control of Industrial Major Accident Hazards) Regulations 1996",
    jurisdiction: "Malaysia · Federal",
    regulation: "Regulations 12–23",
    title: "Major-hazard report and emergency planning",
    dutyHolder: "client",
    requirement:
      "For a major hazard installation, use a registered competent person, submit and maintain the industrial activity report, coordinate on-site and off-site emergency plans and control public information and major-accident notification.",
    trigger: "major-hazard",
    expectedEvidence: [
      "Competent person appointment",
      "Industrial activity report",
      "On-site and off-site emergency plans",
      "Emergency exercise and public-information records",
    ],
    sourceUrl: `${cimahUrl}#page=7`,
    sourceLabel: "CIMAH 1996 · Regulations 12–23 · PDF pp. 7–10",
    reviewState,
  },
  {
    id: "MY-OSHA-S30",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Occupational Safety and Health Act 1994 [Act 514]",
    jurisdiction: "Malaysia · Federal",
    regulation: "Section 30",
    title: "Safety and health committee",
    dutyHolder: "client",
    requirement:
      "Establish a safety and health committee at a workplace where forty or more persons are employed, or where directed, and maintain its composition and functions under the committee regulations.",
    trigger: "workforce-40",
    expectedEvidence: [
      "Safety and health committee establishment record",
      "Employer and employee representative appointments",
      "Committee meeting and inspection programme",
      "Issue and recommendation close-out log",
    ],
    sourceUrl: `${oshaUrl}#page=45`,
    sourceLabel: "Act 514 · Section 30; Committee Regulations 1996",
    reviewState,
  },
  {
    id: "MY-SHC-GOVERNANCE",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Occupational Safety and Health (Safety and Health Committee) Regulations 1996",
    jurisdiction: "Malaysia · Federal",
    regulation: "Committee governance",
    title: "Committee constitution, meetings and workplace inspections",
    dutyHolder: "all",
    requirement:
      "Constitute the committee with the prescribed representation, meet at the required frequency, inspect the workplace, investigate relevant events and record recommendations and employer responses.",
    trigger: "workforce-40",
    expectedEvidence: [
      "Committee constitution and membership",
      "Meeting calendar and minutes",
      "Workplace inspection reports",
      "Employer response and close-out evidence",
    ],
    sourceUrl: `${committeeUrl}#page=3`,
    sourceLabel: "Safety and Health Committee Regulations 1996",
    reviewState,
  },
  {
    id: "MY-OSHA-S18B",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Occupational Safety and Health Act 1994 [Act 514]",
    jurisdiction: "Malaysia · Federal",
    regulation: "Section 18B",
    title: "Risk assessment and implemented controls",
    dutyHolder: "all",
    requirement:
      "The employer, self-employed person or principal conducts a risk assessment for safety and health risks to affected persons and implements controls where required.",
    trigger: "always",
    expectedEvidence: [
      "Project or work-package risk assessment",
      "Risk-control implementation record",
      "Risk assessment review record",
    ],
    sourceUrl: `${oshaUrl}#page=28`,
    sourceLabel: "Act 514 · Section 18B · PDF pp. 28–29",
    reviewState,
  },
  {
    id: "MY-CIDB-S25",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Construction Industry Development Board Act 1994 [Act 520]",
    jurisdiction: "Malaysia · Federal",
    regulation: "Section 25",
    title: "Valid contractor registration",
    dutyHolder: "contractor",
    requirement:
      "A person carrying out, undertaking or completing construction work must be registered with CIDB and hold a valid certificate of registration.",
    trigger: "always",
    expectedEvidence: [
      "Current CIDB contractor registration certificate",
      "Registration scope and grade check",
      "Tender or appointment verification record",
    ],
    sourceUrl: `${cidbUrl}#page=27`,
    sourceLabel: "Act 520 · Section 25 · PDF p. 27",
    reviewState,
  },
  {
    id: "MY-CIDB-S34",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Construction Industry Development Board Act 1994 [Act 520]",
    jurisdiction: "Malaysia · Federal",
    regulation: "Section 34",
    title: "Construction contract declaration and levy screen",
    dutyHolder: "contractor",
    requirement:
      "The contractor declares awarded construction contracts to CIDB and determines whether the statutory levy requirements apply.",
    trigger: "always",
    expectedEvidence: [
      "CIDB contract declaration acknowledgement",
      "Contract sum and levy determination",
      "Levy notice and payment evidence where applicable",
    ],
    sourceUrl: `${cidbUrl}#page=34`,
    sourceLabel: "Act 520 · Section 34 · PDF pp. 34–36",
    reviewState,
  },
  {
    id: "MY-CIDB-S34B",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "cross-cutting-duty",
    instrument: "Construction Industry Development Board Act 1994 [Act 520]",
    jurisdiction: "Malaysia · Federal",
    regulation: "Section 34B",
    title: "Contractor duty for construction and building safety",
    dutyHolder: "contractor",
    requirement:
      "The contractor submits required construction information, complies with applicable written law and safeguards the building and construction work during and after construction.",
    trigger: "always",
    expectedEvidence: [
      "CIDB project information submission",
      "Applicable-law compliance register",
      "Construction and post-construction safety assurance records",
    ],
    sourceUrl: `${cidbUrl}#page=36`,
    sourceLabel: "Act 520 · Section 34B · PDF pp. 36–37",
    reviewState,
  },
  {
    id: "SWK-BLDG-BL1",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "approval-gateway",
    instrument: "Buildings Ordinance 1994 [Cap. 8], Fourth Schedule",
    jurisdiction: "Sarawak",
    regulation: "Building By-law 1",
    title: "Building-plan approval pathway",
    dutyHolder: "client",
    requirement:
      "For new building work in Sarawak, confirm the local-authority building-plan submission and approval pathway, including the responsible qualified person.",
    trigger: "always",
    workTypes: ["new-construction"],
    regions: ["sarawak"],
    expectedEvidence: [
      "Qualified person appointment",
      "Local-authority submission receipt",
      "Approved building plans and approval conditions",
    ],
    sourceUrl: `${sarawakBuildingBylawsUrl}#page=1`,
    sourceLabel: "Buildings Ordinance 1994 · Fourth Schedule · By-law 1 · PDF p. 1",
    reviewState,
  },
  {
    id: "SWK-NREO-S11A",
    packId: RELATED_REQUIREMENTS_PACK.id,
    kind: "approval-gateway",
    instrument: "Natural Resources and Environment Ordinance [Cap. 84]",
    jurisdiction: "Sarawak",
    regulation: "Section 11A",
    title: "Sarawak prescribed-activity and EIA screen",
    dutyHolder: "client",
    requirement:
      "Screen the proposed development against Sarawak prescribed activities. Where an impact report is required, preparatory work must not commence before NREB approval.",
    trigger: "always",
    workTypes: ["new-construction"],
    regions: ["sarawak"],
    expectedEvidence: [
      "Signed NREB prescribed-activity screening record",
      "NREB determination or consultation record",
      "Approved impact report and conditions where applicable",
    ],
    sourceUrl: `${sarawakEnvironmentUrl}#page=23`,
    sourceLabel: "Sarawak Cap. 84 · Section 11A · PDF pp. 23–25",
    reviewState,
  },
];
